// Per-job variations section: lists signed variations + "Request variation" button.
// Visible to all roles (field workers can raise; office can manage).
import { useEffect, useState } from "react";
import { FileSignature } from "lucide-react";
import api from "@/lib/api";
import { VariationDialog } from "@/components/VariationDialog";
import { VariationCard } from "@/components/VariationCard";
import { useAuth } from "@/lib/auth";

export function JobVariations({ job }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const canEdit = ["admin", "office", "super_admin"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/jobs/${job.id}/variations`);
      setItems(data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id]);

  return (
    <section className="hard-card p-5" data-testid="job-variations-section">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="label-tag flex items-center gap-1.5">
          <FileSignature className="w-3 h-3" /> Site variations
          {items.length > 0 && (
            <span className="ml-1.5 px-1.5 py-0.5 bg-zinc-900 text-white text-[9px] font-mono">
              {items.length}
            </span>
          )}
        </div>
        <VariationDialog job={job} onCreated={(v) => setItems((p) => [v, ...p])} />
      </div>

      {loading ? (
        <div className="h-24 bg-zinc-100 animate-pulse" data-testid="job-variations-loading" />
      ) : items.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-300 p-6 text-center" data-testid="job-variations-empty">
          <p className="text-sm text-zinc-500">
            No variations yet on this job. Tap <span className="font-bold">Request variation</span> when the customer
            asks for additional work — capture their signature so the office can charge for it.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="job-variations-list">
          {items.map((v) => (
            <VariationCard
              key={v.id}
              variation={v}
              canEdit={canEdit}
              onChanged={(updated) =>
                setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
              }
              onDeleted={(id) => setItems((prev) => prev.filter((x) => x.id !== id))}
            />
          ))}
        </div>
      )}
    </section>
  );
}
